import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { notificationPrefsSchema, type NotificationPrefsInput } from '@auditwire/contracts';
import { Button, Switch } from '@/shared/ui';
import { toMessage } from '@/shared/lib/repository/errors';
import { useUpdateNotificationPrefs } from '../api/useProfile';
import type { NotificationPrefs } from '../model/types';

const ITEMS: { key: keyof NotificationPrefsInput; label: string; hint: string }[] = [
  { key: 'reply', label: 'Replies', hint: 'When someone replies to your comment.' },
  { key: 'newArticle', label: 'New articles', hint: 'When a new article is published.' },
  { key: 'newsletter', label: 'Newsletter', hint: 'The weekly email.' },
  { key: 'weeklyDigest', label: 'Weekly digest', hint: 'A summary of the week’s writing.' },
];

export function NotificationPrefsForm({ prefs }: { prefs: NotificationPrefs }) {
  const updatePrefs = useUpdateNotificationPrefs();
  const { control, handleSubmit } = useForm<NotificationPrefsInput>({
    resolver: zodResolver(notificationPrefsSchema),
    defaultValues: prefs,
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await updatePrefs.mutateAsync(values);
      toast.success('Preferences saved');
    } catch (err) {
      toast.error(toMessage(err, 'Could not save preferences'));
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-2">
      {ITEMS.map((item) => (
        <Controller
          key={item.key}
          control={control}
          name={item.key}
          render={({ field }) => (
            <div className="flex items-center justify-between gap-4 rounded-xl border border-border px-4 py-3">
              <div>
                <p className="text-sm font-medium text-foreground">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.hint}</p>
              </div>
              <Switch checked={field.value} onCheckedChange={field.onChange} aria-label={item.label} />
            </div>
          )}
        />
      ))}
      <div className="pt-3">
        <Button type="submit" disabled={updatePrefs.isPending}>
          {updatePrefs.isPending ? 'Saving…' : 'Save preferences'}
        </Button>
      </div>
    </form>
  );
}
